import React from 'react';
import { Users, Loader2 } from 'lucide-react';
import { useBranchProgress } from '../../hooks/useDues';
import { formatCurrency } from '../../utils/payment-format';

const BranchProgressCard: React.FC = () => {
  const { data, isLoading } = useBranchProgress();
  const year = data?.data?.year;
  const branches = data?.data?.branches ?? [];

  if (isLoading) {
    return (
      <div className="bg-surface-1 rounded-lg shadow-sm p-6 border border-border">
        <h3 className="text-xl font-bold text-text-primary mb-4 font-display">Dues Paid by Branch</h3>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 text-brand animate-spin" />
        </div>
      </div>
    );
  }

  if (branches.length === 0) {
    return null;
  }

  return (
    <div className="bg-surface-1 rounded-lg shadow-sm p-6 border border-border">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
          <Users className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-text-primary font-display">Dues Paid by Branch</h3>
          {year && <p className="text-text-secondary text-xs">{year} Assessment</p>}
        </div>
      </div>

      <div className="space-y-4">
        {(() => {
          const maxCollected = Math.max(...branches.map((b) => b.totalCollected), 0);

          return branches.map((branch) => {
            const barPct = maxCollected > 0
              ? (branch.totalCollected / maxCollected) * 100
              : 0;

            return (
              <div key={branch.branch}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-primary">{branch.branch}</span>
                  <span className="text-xs text-text-secondary">
                    {formatCurrency(branch.totalCollected)} collected
                  </span>
                </div>
                <div className="w-full h-3 bg-surface-2 rounded-full overflow-hidden">
                  <div
                    role="progressbar"
                    aria-valuenow={Math.round(barPct)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${branch.branch}: ${formatCurrency(branch.totalCollected)} collected of ${formatCurrency(branch.totalAssessed)} assessed`}
                    className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-text-secondary">
                    {formatCurrency(branch.totalAssessed)} assessed
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {branch.totalMembers} {branch.totalMembers === 1 ? 'member' : 'members'}
                  </span>
                </div>
            </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export { BranchProgressCard };
